import React, { useMemo, useState } from 'react';
import { ExternalLink, FileText, Layers, Pencil, Radio } from 'lucide-react';
import { Link } from 'react-router-dom';
import { validateExam, type EvaluationExam } from '@interactive-room';

import type { StudioResource } from '../../studioResources';
import { getEvaluationExam, saveEvaluationExam } from '../../examStorage';
import { getSubjectById } from '../../constants/subjectCatalog';
import { editHref } from '../../lib/studioResourceLinks';
import { buildPrintWorksheetUrl } from '../../print/buildPrintWorksheetUrl';
import { PrintWorksheetDocument } from '../../print/PrintWorksheetDocument';
import { getCourseProfileById } from '../../services/courseProfileStorage';
import { buildHostLaunchUrl } from '../../hostLaunch';

export interface CourseMaterialPreviewPaneProps {
  resource: StudioResource | null;
  subjectId: string;
  /** Opcional en biblioteca de asignatura (sin curso). */
  courseId?: string;
  subjectTag?: string;
}

function canLaunchDigital(exam: EvaluationExam | null, kind: StudioResource['kind']): boolean {
  if (!exam || exam.blocks.length === 0) return false;
  if (kind === 'class') return true;
  if (exam.deliveryFormat === 'interactive') return true;
  if (exam.deliveryProfile === 'interactive_class') return true;
  // Evaluaciones / fichas con mecánicas de sala también pueden lanzarse.
  if (kind === 'exam' && exam.resourceType !== 'guide') return true;
  return false;
}

/**
 * Panel derecho WYSIWYG: misma renderización que la vista de impresión.
 * Lanzar digital vive aquí (no en el editor de hoja).
 */
export function CourseMaterialPreviewPane({
  resource,
  subjectId,
  courseId,
  subjectTag,
}: CourseMaterialPreviewPaneProps) {
  const subject = getSubjectById(subjectId);
  const course = courseId ? getCourseProfileById(courseId) : undefined;
  const [examVersion, setExamVersion] = useState(0);
  const exam = useMemo(() => {
    void examVersion;
    if (!resource) return null;
    if (resource.kind === 'activity') return null;
    return getEvaluationExam(resource.id);
  }, [resource, examVersion]);

  if (!resource) {
    return (
      <div className="course-material-preview course-material-preview--empty">
        <div className="course-material-preview__silhouette" aria-hidden>
          <span className="course-material-preview__silhouette-bar" />
          <span className="course-material-preview__silhouette-line" />
          <span className="course-material-preview__silhouette-line course-material-preview__silhouette-line--short" />
          <span className="course-material-preview__silhouette-block" />
        </div>
        <p className="course-material-preview__empty-title">Vista previa</p>
        <p className="course-material-preview__empty-hint">
          Selecciona un material de la biblioteca para verlo aquí.
        </p>
      </div>
    );
  }

  const fullHref = editHref(resource, subjectId, courseId);
  const printHref =
    resource.kind === 'guide' || resource.kind === 'exam'
      ? buildPrintWorksheetUrl(resource.id)
      : null;
  const canWysiwyg = Boolean(exam && (resource.kind === 'guide' || resource.kind === 'exam' || resource.kind === 'class'));
  const launchHref =
    exam && canLaunchDigital(exam, resource.kind)
      ? buildHostLaunchUrl({
          kind: 'exam',
          resourceId: exam.id,
          title: exam.title,
          subjectId,
        })
      : null;

  const persistSettings = (patch: Partial<EvaluationExam['settings']>) => {
    if (!exam) return;
    const next = validateExam({
      ...exam,
      settings: { ...exam.settings, ...patch },
    });
    saveEvaluationExam(next);
    setExamVersion((v) => v + 1);
  };

  return (
    <div className="course-material-preview">
      <header className="course-material-preview__chrome">
        <div className="min-w-0 flex-1">
          <p className="course-material-preview__eyebrow">
            {resource.kind === 'guide'
              ? 'Guía'
              : resource.kind === 'exam'
                ? 'Evaluación'
                : resource.kind === 'class'
                  ? 'Clase'
                  : 'Actividad'}
          </p>
          <h2 className="course-material-preview__title">{resource.title}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {launchHref ? (
            <Link to={launchHref} className="course-material-preview__open course-material-preview__open--live">
              <Radio className="mr-1 h-3.5 w-3.5" aria-hidden />
              Lanzar digital
            </Link>
          ) : null}
          {printHref ? (
            <Link
              to={printHref}
              target="_blank"
              rel="noopener noreferrer"
              className="course-material-preview__open"
            >
              Imprimir
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" aria-hidden />
            </Link>
          ) : null}
          <Link to={fullHref} className="course-material-preview__open course-material-preview__open--primary">
            <Pencil className="mr-1 h-3.5 w-3.5" aria-hidden />
            Editar
          </Link>
        </div>
      </header>

      {exam && launchHref ? (
        <details className="course-material-preview__session no-print">
          <summary>Opciones de sala digital</summary>
          <div className="course-material-preview__session-opts">
            <label>
              <input
                type="checkbox"
                checked={exam.settings.integrityMode === 'STRICT'}
                onChange={(e) =>
                  persistSettings({ integrityMode: e.target.checked ? 'STRICT' : 'FLEXIBLE' })
                }
              />
              Integridad estricta (anti-foco)
            </label>
            <label>
              <input
                type="checkbox"
                checked={exam.settings.advanceMode === 'AUTONOMOUS'}
                onChange={(e) =>
                  persistSettings({ advanceMode: e.target.checked ? 'AUTONOMOUS' : 'HOST' })
                }
              />
              Avance autónomo
            </label>
            <label>
              <input
                type="checkbox"
                checked={exam.settings.shuffleBlocks}
                onChange={(e) => persistSettings({ shuffleBlocks: e.target.checked })}
              />
              Mezclar bloques e ítems
            </label>
          </div>
        </details>
      ) : null}

      <div className="course-material-preview__body">
        {canWysiwyg && exam ? (
          <div className="course-material-preview__wysiwyg">
            <PrintWorksheetDocument
              exam={exam}
              subjectName={subject?.name}
              subjectTag={subjectTag ?? course?.subjectTag ?? subject?.tag}
              schoolName={course?.schoolName}
            />
          </div>
        ) : (
          <div className="course-material-preview course-material-preview--empty" style={{ minHeight: '18rem', border: 'none' }}>
            <Layers className="h-8 w-8 text-[var(--micelio-terracota,#e07a5f)]" strokeWidth={1.5} />
            <p className="course-material-preview__empty-title mt-3">Sin vista WYSIWYG</p>
            <p className="course-material-preview__empty-hint">
              Este recurso se abre en su editor propio.
            </p>
            <Link to={fullHref} className="sala-btn-hero mt-6 inline-flex text-xs">
              <FileText className="mr-1.5 h-3.5 w-3.5" />
              Abrir editor
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
